import React, { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    Modal,
    Box,
    TextField,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { Edit, Delete, Close } from '@mui/icons-material';
import api from '../../config/axios';
import { toast } from 'react-toastify';
import ModalContainer from '../containers/ModalContainer';

const BitcoinAddressCard = () => {
    const [coins, setCoins] = useState({});
    const [bitcoinAddress, setBitcoinAddress] = useState('');
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [qrCodeBase64, setQrCodeBase64] = useState('');
    const [newAddress, setNewAddress] = useState('');
    const [minConfirmations, setMinConfirmations] = useState(6);
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCoin, setSelectedCoin] = useState({});
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
    };

    const getAddress = async () => {
        await api
            .get('/admin-settings/get-address')
            .then((res) => {
                setCoins(res.data.data);
                setBitcoinAddress(res.data.data.mainAddress);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        getAddress();
    }, []);

    const handleAddAddress = async () => {
        setIsLoading(true);
        try {
            // This is where you would make your API call to save the address
            const response = await api.post('/admin-settings/add-address', {
                mainAddress: newAddress,
                coin: 'BTC',
                minConfirmations,
            });

            if (response.status === 201) {
                setQrCodeBase64(response.data.qrCode);
            } else {
                toast.error('Error: ' + response.data.message);
            }
        } catch (error) {
            console.error('Error adding address:', error);
            toast.error('Failed to add address. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmAdd = async () => {
        setIsLoading(true);
        try {
            setTimeout(() => {
                getAddress();
                setIsAddressModalOpen(false);
                setQrCodeBase64('');
                setNewAddress('');
                toast.success('Address confirmed successfully');
            }, 2000);
        } catch (error) {
            console.error('Error confirming address:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateAddress = async () => {
        setIsLoading(true);
        try {
            const response = await api.put('/admin-settings/update-address', {
                mainAddress: selectedCoin.mainAddress,
                coin: 'BTC',
                minConfirmations: selectedCoin.minConfirmations,
                otp,
            });

            if (response.status === 200) {
                toast.success('Address Updated successfully');
                getAddress();
                setIsEditModalOpen(false);
                setNewAddress('');
                setOtp('');
                setSelectedCoin({});
            } else {
                toast.error('Error: ' + response.data.message);
            }
        } catch (error) {
            console.error('Error updating address:', error);
            toast.error('Error: ' + error.response.data.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteAddress = async () => {
        setIsLoading(true);
        try {
            // This is where you would make your API call to delete the address
            const response = await api.post('/admin-settings/delete-address', {
                coin: 'BTC',
                otp,
            });

            if (response.status === 200) {
                getAddress();
                toast.success('Address Deleted successfully');
                setIsDeleteModalOpen(false);
                setSelectedCoin({});
                setOtp('');
            } else {
                toast.error('Error: ' + response.data.message);
            }
        } catch (error) {
            console.error('Error deleting address:', error);
            toast.error('Failed to delete address. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Card sx={{ margin: '0 auto' }}>
            <CardContent>
                <Box display="flex" flexDirection="row" alignItems="center" justifyContent={'space-between'}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <img src={'/assets/images/bitcoin.svg'} alt="Bitcoin Logo" width="80" height="40" />
                        <Typography variant="h6" component="div" sx={{ mt: 1 }}>
                            Bitcoin
                        </Typography>
                    </Box>

                    {bitcoinAddress ? (
                        <Box>
                            <Typography variant="h6" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                {bitcoinAddress.slice(0, 8)}................{bitcoinAddress.slice(-4)}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="h6" color="text.secondary" align="center">
                            No address added yet
                        </Typography>
                    )}
                </Box>
            </CardContent>

            <CardActions sx={{ justifyContent: 'end' }}>
                {bitcoinAddress ? (
                    <>
                        <IconButton
                            color="primary"
                            onClick={() => {
                                setSelectedCoin(coins);
                                setIsEditModalOpen(true);
                            }}>
                            <Edit />
                        </IconButton>
                        <IconButton
                            color="error"
                            onClick={() => {
                                setSelectedCoin(coins);
                                setIsDeleteModalOpen(true);
                            }}>
                            <Delete />
                        </IconButton>
                    </>
                ) : (
                    <Button variant="contained" color="primary" onClick={() => setIsAddressModalOpen(true)}>
                        Add Address
                    </Button>
                )}
            </CardActions>

            {/* Add Address Modal */}
            <ModalContainer
                open={isAddressModalOpen}
                onClose={() => {
                    if (!isLoading) {
                        setIsAddressModalOpen(false);
                        setQrCodeBase64('');
                        setNewAddress('');
                    }
                }}
                hideActions
                title="Add Platform address">
                <Box mt={4}>
                    {qrCodeBase64 ? (
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                            <Typography variant="body2">Scan this QR code with Google Authenticator</Typography>
                            <img src={`${qrCodeBase64}`} alt="QR Code" style={{ width: '200px', height: '200px' }} />
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleConfirmAdd}
                                disabled={isLoading}>
                                {isLoading ? <CircularProgress size={24} /> : 'Confirm'}
                            </Button>
                        </Box>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Bitcoin Address"
                                fullWidth
                                value={newAddress}
                                onChange={(e) => setNewAddress(e.target.value)}
                                disabled={isLoading}
                            />
                            <TextField
                                label="Min Confirmations"
                                fullWidth
                                value={minConfirmations}
                                onChange={(e) => setMinConfirmations(e.target.value)}
                                disabled={isLoading}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleAddAddress}
                                disabled={!newAddress || isLoading}>
                                {isLoading ? <CircularProgress size={24} /> : 'Continue'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </ModalContainer>

            {/* Edit Address Modal */}
            <ModalContainer
                open={isEditModalOpen}
                onClose={() => {
                    if (!isLoading) {
                        setIsEditModalOpen(false);
                    }
                }}
                hideActions
                title="Update Platform address">
                <Box display="flex" flexDirection="column" gap={2} mt={4}>
                    <TextField
                        label="Bitcoin Address"
                        fullWidth
                        value={selectedCoin.mainAddress}
                        onChange={(e) => setSelectedCoin({ ...selectedCoin, mainAddress: e.target.value })}
                        disabled={isLoading}
                    />
                    <TextField
                        label="Min Confirmations"
                        fullWidth
                        value={selectedCoin.minConfirmations}
                        onChange={(e) => setSelectedCoin({ ...selectedCoin, minConfirmations: e.target.value })}
                        disabled={isLoading}
                    />
                    <TextField
                        label="6-digit OTP"
                        fullWidth
                        value={otp}
                        onChange={(e) => {
                            // Only allow numeric input and max 6 digits
                            const otpValue = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtp(otpValue);
                        }}
                        placeholder="Enter 6-digit code from Google Authenticator"
                        disabled={isLoading}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleUpdateAddress}
                        disabled={otp.length !== 6 || isLoading}>
                        {isLoading ? <CircularProgress size={24} /> : 'Update'}
                    </Button>
                </Box>
            </ModalContainer>

            {/* Delete Address Modal */}
            <ModalContainer
                open={isDeleteModalOpen}
                onClose={() => {
                    if (!isLoading) {
                        setIsDeleteModalOpen(false);
                    }
                }}
                hideActions
                title="Update Platform address">
                <Box display="flex" flexDirection="column" gap={2} mt={4}>
                    <Typography variant="body2">Are you sure you want to delete this Bitcoin address?</Typography>

                    <TextField
                        label="6-digit OTP"
                        fullWidth
                        value={otp}
                        onChange={(e) => {
                            // Only allow numeric input and max 6 digits
                            const otpValue = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtp(otpValue);
                        }}
                        placeholder="Enter 6-digit code from Google Authenticator"
                        disabled={isLoading}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleDeleteAddress}
                        disabled={otp.length !== 6 || isLoading}>
                        {isLoading ? <CircularProgress size={24} /> : 'Confirm Delete'}
                    </Button>
                </Box>
            </ModalContainer>
        </Card>
    );
};

export default BitcoinAddressCard;
